import * as amplitude from "@amplitude/analytics-browser";
import { useApolloClient } from "@apollo/client";
import { Button, Flex, MenuItem, Spinner, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import TagManager from "react-gtm-module";
import { useNavigate } from "react-router-dom";
import { acceptOffer } from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useOrganization } from "src/hooks/useOrganization";
import { refreshFormAdmissionsAfterMutation } from "src/scenes/parent/formTemplates/admissions";
import {
  GET_OFFERS_FORM_CONTEXT,
  GET_PARENT_ASSIGNED_FORMS,
  GET_PARENT_FORMS_INDEX,
} from "src/scenes/parent/formTemplates/graphql/queries";
import { useFormTemplateHint } from "src/scenes/parent/hint";
import { useNextStep } from "src/scenes/parent/nextStep/NextStepProvider";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";

interface Props {
  acceptedOfferSchoolName?: string;
  formId: uuid;
  formKey: string;
  formTemplateId: uuid;
  offerId: uuid;
  school: string;
  schoolReferenceId: string | null;
  studentName: string;
  enrollmentPeriodKey?: string | null;
  isButton?: boolean;
  isDisabled?: boolean;
  onActionPendingChange?: (pending: boolean) => void;
}

export const AcceptOffer = ({
  acceptedOfferSchoolName,
  formId,
  formKey,
  formTemplateId,
  offerId,
  school,
  schoolReferenceId,
  studentName,
  enrollmentPeriodKey,
  isButton = false,
  isDisabled = false,
  onActionPendingChange,
}: Props) => {
  const apolloClient = useApolloClient();
  const nextStep = useNextStep();

  const toast = useAvelaToast({ position: "bottom" });
  const navigate = useNavigate();
  const organization = useOrganization();

  const { pushEvent } = useFormTemplateHint();
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = React.useState(false);

  const { confirm, confirmationDialog, setBody, setHeader } =
    useConfirmationDialog({
      header: null,
      body: null,
      cancelButton: {
        label: "No, cancel",
      },
      confirmButton: {
        label: "Yes, accept",
        colorScheme: "primary",
      },
      translate: true,
    });

  const handleOnClick = async (evt: React.MouseEvent) => {
    setHeader(<Text>Accept offer?</Text>);
    if (acceptedOfferSchoolName) {
      setBody(
        <>
          <Text>
            {`Accepting the offer from ${school} will decline the previously accepted offer from ${acceptedOfferSchoolName}.`}
          </Text>
          <Text pt={8}>This can't be undone nor recovered.</Text>
        </>
      );
    } else {
      setBody(
        <Text>{`Accepting the offer from ${school} will not affect any other offers made to ${studentName}.`}</Text>
      );
    }

    if (await confirm()) {
      setIsLoading(true);
      onActionPendingChange?.(true);
      try {
        amplitude.track("Accept offer confirmed");
        const response = await acceptOffer({
          path: { formId },
          body: {
            data: {
              offer_id: offerId,
            },
          },
        });

        nextStep.process(response.data?.data.assignedForms);
        apolloClient.refetchQueries({
          include: [
            GET_OFFERS_FORM_CONTEXT,
            GET_PARENT_FORMS_INDEX,
            GET_PARENT_ASSIGNED_FORMS,
          ],
        });
        await refreshFormAdmissionsAfterMutation(queryClient, formId);

        TagManager.dataLayer({
          dataLayer: {
            event: "offer-accepted",
            offer_form_key: formKey,
            offer_school_reference_id: schoolReferenceId,
            offer_enrollment_period_key: enrollmentPeriodKey,
          },
        });

        toast({
          title: "Hooray!",
          description: "Offer accepted, nicely done!",
          status: "success",
        });
        navigate(organization.map(Url.Parent.index).withDefault("#"));

        pushEvent({
          type: "offer",
          formTemplateId,
          studentName,
          status: GQL.offer_status_enum.Accepted,
        });
      } catch (err: any) {
        console.error(err);

        if ("error" in err && "httpStatus" in err.error) {
          const httpStatus = err.error.httpStatus;
          if (httpStatus === 400) {
            toast.error({
              title: "Offer has expired",
              description: "It can't be accepted or declined anymore",
            });
          } else if (httpStatus === 403) {
            toast.error({
              title: "You are not authorized to accept this offer",
              description: "You are not allowed to perform this action.",
            });
          } else if (httpStatus === 404) {
            toast.error({
              title: "Offer not found",
              description: "The offer you are trying to accept does not exist.",
            });
          } else {
            toast.genericError();
          }
        } else {
          toast.genericError();
        }
      } finally {
        setIsLoading(false);
        onActionPendingChange?.(false);
      }
    }
  };

  if (isButton) {
    return (
      <>
        <Button
          size="sm"
          width="50%"
          onClick={(e) => {
            amplitude.track("Accept offer clicked", {
              button_location: "offers_page",
            });
            handleOnClick(e);
          }}
          isLoading={isLoading}
          isDisabled={isDisabled && !isLoading}
        >
          Accept offer
        </Button>
        {confirmationDialog}
      </>
    );
  }

  return (
    <>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={(e) => {
          amplitude.track("Accept offer clicked", {
            button_location: "overflow_menu",
          });
          handleOnClick(e);
        }}
        isDisabled={isDisabled && !isLoading}
      >
        <Flex align="center" gap={2}>
          {isLoading && <Spinner size="xs" flexShrink={0} />}
          Accept offer
        </Flex>
      </MenuItem>
      {confirmationDialog}
    </>
  );
};
